import React from 'react';
import { useExpeditionLog } from '../../stores/expeditionLog';
import { visibleRows, decisionStatuses, type LogFilter } from './expeditionLogReducer';
import { ExpeditionLogEntry } from './ExpeditionLogEntry';
import { usePersonaChatUI } from '../../contexts/PersonaChatUIContext';

const CHIPS: { key: LogFilter; label: string }[] = [
  { key: 'all', label: 'All' }, { key: 'governance', label: 'Gov' },
  { key: 'turns', label: 'Turns' }, { key: 'squad', label: 'Squad' },
];

export const ExpeditionLog: React.FC<{ sessionId: string; darkMode: boolean; variant?: 'drawer' | 'embedded' }> =
({ sessionId, darkMode, variant = 'drawer' }) => {
  const { logState, setFilter, toggleSquad, inFlight, resolve } = useExpeditionLog();
  const { focusNode } = usePersonaChatUI() as any;
  const rows = visibleRows(logState);
  const statuses = decisionStatuses(logState);

  const govProps = (entry: { event_type: string; target: Record<string, unknown> }) => {
    if (entry.event_type !== 'governor_proposed') return {};
    const did = (entry.target as any)?.decision_id as string | undefined;
    if (!did) return {};
    return {
      actionable: statuses[did] === 'pending',
      inFlight: inFlight.has(did),
      onApprove: () => resolve(sessionId, did, 'approve'),
      onDismiss: () => resolve(sessionId, did, 'dismiss'),
    };
  };

  return (
    <div className={`flex flex-col min-h-0 min-w-0 h-full text-sm ${
      variant === 'drawer' ? 'w-72 shrink-0 border-l' : 'w-full'} ${
      darkMode ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-800'}`}
      data-testid="expedition-log">
      {variant === 'drawer' && (
        <div className="shrink-0 px-3 py-2 font-medium flex items-center justify-between">
          <span>Expedition Log</span>
        </div>
      )}
      <div className="shrink-0 px-2 pb-2 flex gap-1">
        {CHIPS.map(c => (
          <button key={c.key} type="button" onClick={() => setFilter(c.key)}
            className={`px-2 py-0.5 rounded text-[11px] ${logState.filter === c.key
              ? 'bg-blue-600 text-white'
              : darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            {c.label}
          </button>
        ))}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-1 pb-2">
        {rows.map((r, i) => r.kind === 'event' ? (
          <ExpeditionLogEntry key={r.entry.seq} entry={r.entry} darkMode={darkMode}
            onFocus={(tk) => focusNode?.(tk)} {...govProps(r.entry)} />
        ) : (
          <div key={`sq-${r.squadId}-${i}`} className="px-2 py-1 text-[11px]">
            <button type="button" onClick={() => toggleSquad(r.squadId)}
              className="flex gap-2 items-center w-full text-left">
              <span className="opacity-70">▣</span>
              <span>Squad — {r.memberCount} member events {r.expanded ? '▾' : '▸'}</span>
            </button>
            {r.expanded && r.members.map(m => (
              <ExpeditionLogEntry key={m.seq} entry={m} darkMode={darkMode}
                onFocus={(tk) => focusNode?.(tk)} />
            ))}
          </div>
        ))}
        {rows.length === 0 && (
          <div className="px-3 py-6 text-center text-[11px] opacity-50">No events yet</div>
        )}
      </div>
    </div>
  );
};
